/**
 * Cliente HTTP da API CAINT (porta + adapter, doc 13.5). O app depende da
 * interface `ApiClient`; o adapter `HttpApiClient` usa `fetch` (injetável, para
 * testes). Mesma origem em produção: base `/api` (sem CORS).
 */
import type { DashboardImgr } from "@caint/data";
import type {
  AbrirCicloDTO,
  AssinaturaConfirmarDTO,
  AssinaturaUploadDTO,
  AtualizarUsuarioDTO,
  CachesImgq,
  CicloPublico,
  CriarUsuarioDTO,
  EditarCicloDTO,
  EscopoData,
  EvidenciaConfirmarDTO,
  EvidenciaUploadDTO,
  FormularioPublico,
  ImgrRespostaDTO,
  MatrizUnidade,
  MatrizUploadDTO,
  OperacaoSyncDTO,
  PlanoAcao,
  PlanoEvidenciaConfirmarDTO,
  PlanoEvidenciaUploadDTO,
  PlanoItem,
  RelatorioDTO,
  ResponderPlanoItemDTO,
  ResumoMatrizUnidade,
  SupervisaoPushDTO,
  SupervisaoRemota,
  UsuarioPublico,
} from "@caint/shared";
import type { UsuarioAutenticado } from "../state/useAppState.ts";

export interface LoginResposta {
  readonly token: string;
  readonly usuario: {
    id: string;
    nome: string;
    papel: string;
    eixo: string | null;
    unidadeId?: string | null;
    /** Só para o papel `unidade`: cargo e setores no Portal do Plano de Ação. */
    cargoUnidade?: string | null;
    setores?: string[] | null;
  };
}

/** `rejeitada`: o servidor recusou (ciclo encerrado/fora da janela) — não reenviar. */
export type StatusOperacao = "aplicada" | "duplicada" | "rejeitada";
export interface ResultadoPushItem {
  readonly operacaoId: string;
  readonly status: StatusOperacao;
}

/** Avaliação como devolvida pelo /sync/pull (subconjunto que o cliente usa). */
export interface AvaliacaoRemota {
  readonly id: string;
  /** Ciclo (visita) ao qual a avaliação pertence — chave do IMGR público do ciclo. */
  readonly cicloId: string;
  readonly unidadeId: string;
  readonly estado: string;
  readonly atualizadoEm: string;
  /** QACI/IMS consolidados DAQUELE ciclo (revalidados no servidor); `null` se ainda não há. */
  readonly qaciFinal: number | null;
  readonly ims: number | null;
  /**
   * Índices do ciclo revalidados no SERVIDOR a cada push. Já vinham no payload
   * do `/sync/pull`; só não estavam declarados aqui. São a fonte barata do IMGQ
   * por ciclo em telas de rede (recalcular 350 itens × N ciclos no cliente não
   * escala). `null` ⇒ o ciclo ainda não tem base calculada.
   */
  readonly cachesInicial?: CachesImgq | null;
  readonly cachesFinal?: CachesImgq | null;
  readonly ciclo: { status: string; dataInicio: string | null; dataFim: string | null } | null;
  readonly respostas: readonly {
    itemId: string;
    mi: string | null;
    mf: string | null;
    evidenciaMi: string | null;
    evidenciaMf: string | null;
    tipoMi: string | null;
    tipoMf: string | null;
    fonteMi: string | null;
    fonteMf: string | null;
    ts: string;
  }[];
  /** Escopo do ciclo (setores ativos) revalidado no servidor; `null` ⇒ todos (compat). */
  readonly escopo: EscopoData | null;
  readonly supervisao: SupervisaoRemota | null;
}

/** Upload pré-assinado (assinatura eletrônica: só o arquivo em si). */
export interface UploadPreparado {
  readonly id: string;
  readonly chaveB2: string;
  readonly uploadUrl: string;
  readonly expiraSegundos: number;
}

/** Presign de evidência: traz também o destino da miniatura, assinado no mesmo
 *  round-trip. O cliente usa se tiver conseguido gerar a miniatura. */
export interface UploadEvidenciaPreparado extends UploadPreparado {
  readonly chaveMiniatura: string;
  readonly uploadUrlMiniatura: string;
}

/** Assinatura eletrônica do usuário (URL presigned de download + metadados). */
export interface AssinaturaView {
  readonly url: string;
  readonly contentType: string;
  readonly atualizadoEm: string;
}

/** Evidência listada por /evidencias (com URL de download pré-assinada). */
export interface EvidenciaRemota {
  readonly id: string;
  readonly itemId: string;
  readonly momento: string;
  readonly contentType: string;
  readonly tamanhoBytes: number;
  readonly url: string;
  /** Miniatura (~240 px) gerada no upload. `null` para PDF, para foto antiga
   *  ainda não processada pelo backfill, ou se a geração falhou — nesses casos
   *  a galeria cai na `url`. */
  readonly urlMiniatura?: string | null;
  readonly criadoEm: string;
}

/** Evidência de um item do Plano de Ação (com URL de download pré-assinada). */
export interface PlanoEvidenciaRemota {
  readonly id: string;
  readonly contentType: string;
  readonly tamanhoBytes: number;
  readonly url: string;
  readonly urlMiniatura?: string | null;
  readonly criadoEm: string;
  /** Quem subiu (atribuição/auditoria): id + nome + cargo resolvidos. */
  readonly criadoPor: string | null;
  readonly criadoPorNome: string | null;
  readonly criadoPorCargo: string | null;
}

/** Um evento da trilha de auditoria unificada (admin/coord/audin/presidência). */
export interface EventoAuditoria {
  readonly id: string;
  readonly em: string;
  readonly autorId: string | null;
  readonly autorNome: string | null;
  readonly autorPapel: string | null;
  readonly acao: string;
  readonly entidade: string;
  readonly entidadeId: string | null;
  readonly avaliacaoId: string | null;
  readonly unidadeId: string | null;
  readonly resumo: string | null;
  readonly detalhe: unknown;
}
/** Filtros do feed de auditoria (todos opcionais). `offset`/`limite` = paginação. */
export interface FiltroAuditoria {
  acao?: string;
  autorId?: string;
  entidade?: string;
  entidadeId?: string;
  avaliacaoId?: string;
  q?: string;
  de?: string;
  ate?: string;
  offset?: number;
  limite?: number;
}
export interface RespostaAuditoria {
  readonly eventos: EventoAuditoria[];
  readonly temMais: boolean;
}

/* ---------------- Migração de catálogo (Workbench) ---------------- */
export interface LoteRemoto {
  id: string;
  titulo: string;
  origem: string;
  versaoBase: string;
  versaoAlvo: string;
  estado: string;
  criadoEm: string;
  aprovadoPor: string | null;
  executadoPor: string | null;
}
export interface ItemMigracaoRemoto {
  id: string;
  setor: string;
  codigoOrigem: string;
  codigoDestino: string | null;
  tipoProposto: string;
  similaridade: number | null;
  temAvaliacao: boolean;
  perguntaAtual: string | null;
  perguntaNova: string | null;
  camposTexto: Record<string, string> | null;
  decisao: string;
  /** Cache do impacto (já vem na resposta de /migracao/lotes/:id; pode ser null
   * quando ainda não calculado). Alimenta o resumo natural sem chamadas extras. */
  nRespostas: number | null;
  nEvidencias: number | null;
}
export interface LoteDetalheRemoto {
  lote: LoteRemoto;
  itens: ItemMigracaoRemoto[];
  resumo: { total: number; decididos: number; pendentes: number };
}
export interface ImpactoRespostaRemota {
  avaliacaoId: string;
  unidadeNome: string;
  mi: string | null;
  mf: string | null;
  evidenciaMi: string | null;
  autor: string | null;
}
export interface ImpactoEvidenciaRemota {
  id: string;
  avaliacaoId: string;
  momento: string;
  contentType: string;
  tamanhoBytes: number;
  url: string | null;
}
export interface ImpactoRemoto {
  itemId: string;
  respostas: ImpactoRespostaRemota[];
  evidencias: ImpactoEvidenciaRemota[];
  avaliacoes: string[];
  b2Indisponivel: boolean;
}
export interface ConflitoRemoto {
  codigo: string;
  nivel: "bloqueio" | "aviso";
  motivo: string;
}
export interface PreflightRemoto {
  bloqueios: ConflitoRemoto[];
  avisos: ConflitoRemoto[];
  resumo: { recodes: number; textos: number };
}
export interface ManifestoRemoto {
  loteId: string;
  versaoAlvo: string;
  texto: unknown[];
  recode: { de: string; para: string; respostasMovidas: number }[];
  aguardandoPublicacao: boolean;
  revalidacaoPendente: boolean;
}
export interface ResultadoExecucaoRemoto {
  resumo: {
    movimentos: { codigoOrigem: string; codigoDestino: string; nRespostas: number; nEvidencias: number }[];
    avaliacoesRevalidadas: string[];
  };
  manifesto: ManifestoRemoto;
}
export type DecisaoMigracaoValor = "manter" | "migrar_texto" | "migrar_recode" | "rejeitar";

export interface ApiClient {
  /**
   * Registra um callback disparado quando uma requisição AUTENTICADA recebe 401
   * (token expirado/ inválido). Serve para deslogar globalmente e voltar ao
   * login, em vez de espalhar "Não autorizado" em cada tela. O 401 do próprio
   * login (sem token) NÃO dispara — ali é credencial errada, não sessão vencida.
   */
  aoNaoAutorizado?(handler: () => void): void;
  login(matricula: string, senha: string): Promise<LoginResposta>;
  push(operacoes: readonly OperacaoSyncDTO[], token: string): Promise<ResultadoPushItem[]>;
  pull(desde: string | null, token: string): Promise<AvaliacaoRemota[]>;
  pushSupervisao(dto: SupervisaoPushDTO, token: string): Promise<RespostaSupervisao>;
  presignEvidencia(dto: EvidenciaUploadDTO, token: string): Promise<UploadEvidenciaPreparado>;
  confirmarEvidencia(dto: EvidenciaConfirmarDTO, token: string): Promise<void>;
  listarEvidencias(avaliacaoId: string, token: string): Promise<EvidenciaRemota[]>;
  /** Apaga uma foto de evidência (hard-delete + auditoria). 404 ⇒ já removida. */
  removerEvidencia(id: string, avaliacaoId: string, token: string): Promise<void>;
  listarUsuarios(token: string): Promise<UsuarioPublico[]>;
  criarUsuario(dto: CriarUsuarioDTO, token: string): Promise<UsuarioPublico>;
  atualizarUsuario(id: string, dto: AtualizarUsuarioDTO, token: string): Promise<UsuarioPublico>;
  redefinirSenha(id: string, senha: string, token: string): Promise<void>;
  /**
   * Senha provisória para reenviar o acesso. `null` quando a pessoa já entrou
   * (o primeiro login apaga) ou trocou a senha — daí só resta o hash.
   */
  senhaProvisoria(id: string, token: string): Promise<{ senha: string | null }>;
  /** Auto-serviço: o próprio usuário troca a senha (confirma a atual). */
  trocarSenha(senhaAtual: string, novaSenha: string, token: string): Promise<void>;
  listarCiclos(token: string): Promise<CicloPublico[]>;
  abrirCiclo(dto: AbrirCicloDTO, token: string): Promise<{ id: string; avaliacaoId: string }>;
  encerrarCiclo(id: string, token: string): Promise<void>;
  /** Edita datas/status de um ciclo (estender prazo, reabrir, encerrar). */
  editarCiclo(id: string, dto: EditarCicloDTO, token: string): Promise<void>;
  /** Edita o escopo (setores) do ciclo — gestão, independe de aberto/encerrado. */
  salvarEscopoCiclo(avaliacaoId: string, escopo: EscopoData, token: string): Promise<void>;
  /** Formulário público IMGR (sem auth): metadados pelo token. */
  formularioPublico(token: string): Promise<FormularioPublico>;
  /** Envia uma resposta anônima do questionário (sem auth). */
  enviarImgr(token: string, dto: ImgrRespostaDTO): Promise<void>;
  /** Resultado consolidado (IMGR) de um ciclo (autenticado). */
  resultadoImgr(cicloId: string, token: string): Promise<DashboardImgr>;
  /** Matriz da unidade (roster esperado): lê (autenticado) e grava (admin/coord). */
  obterMatriz(unidadeId: string, token: string): Promise<MatrizUnidade | null>;
  salvarMatriz(unidadeId: string, dto: MatrizUploadDTO, token: string): Promise<void>;
  /** Unidades que já têm quadro, com data — alimenta a busca da tela de upload. */
  listarMatrizes(token: string): Promise<ResumoMatrizUnidade[]>;
  /** PUT direto numa URL pré-assinada (ex.: bucket B2). URL absoluta, fora da base. */
  enviarArquivo(url: string, arquivo: Blob, contentType: string): Promise<void>;
  /* Migração de catálogo */
  migracaoIngerir(proposta: unknown, token: string): Promise<{ loteId: string }>;
  migracaoListar(token: string): Promise<LoteRemoto[]>;
  migracaoLote(id: string, token: string): Promise<LoteDetalheRemoto>;
  migracaoImpacto(itemId: string, token: string): Promise<ImpactoRemoto>;
  migracaoDecidir(
    loteId: string,
    itemId: string,
    decisao: DecisaoMigracaoValor,
    codigoDestino: string | null,
    token: string,
  ): Promise<void>;
  migracaoAprovar(id: string, token: string): Promise<void>;
  migracaoPreflight(id: string, token: string): Promise<PreflightRemoto>;
  migracaoExecutar(id: string, token: string): Promise<ResultadoExecucaoRemoto>;
  migracaoReverter(id: string, token: string): Promise<ResultadoExecucaoRemoto["resumo"]>;
  migracaoManifesto(id: string, token: string): Promise<ManifestoRemoto | null>;
  /* Plano de Ação (FACR) */
  /** Planos da unidade logada (papel `unidade`). */
  planosDaUnidade(token: string): Promise<PlanoAcao[]>;
  /** Plano de uma avaliação (papéis internos); `null` se ainda não há itens. */
  planoDaAvaliacao(avaliacaoId: string, token: string): Promise<PlanoAcao | null>;
  /** Responde/edita um item-ação (FACR). */
  responderPlanoItem(id: string, dto: ResponderPlanoItemDTO, token: string): Promise<PlanoItem>;
  /** Regenera o plano de uma avaliação a partir das respostas atuais (interno). */
  regerarPlano(avaliacaoId: string, token: string): Promise<PlanoAcao>;
  /* Relatório institucional (Relatório Preliminar de Maturidade) */
  /** Relatório da unidade logada (papel `unidade`) — só após validação AUDIN. */
  relatorioDaUnidade(token: string): Promise<RelatorioDTO>;
  /** Relatório de uma avaliação (papéis internos). */
  relatorioDaAvaliacao(avaliacaoId: string, token: string): Promise<RelatorioDTO>;
  /* Assinatura eletrônica (imagem) do próprio usuário */
  presignAssinatura(dto: AssinaturaUploadDTO, token: string): Promise<UploadPreparado>;
  confirmarAssinatura(dto: AssinaturaConfirmarDTO, token: string): Promise<void>;
  obterAssinatura(token: string): Promise<AssinaturaView | null>;
  removerAssinatura(token: string): Promise<void>;
  /* Evidências do Plano de Ação (bucket B2 próprio) */
  presignPlanoEvidencia(dto: PlanoEvidenciaUploadDTO, token: string): Promise<UploadEvidenciaPreparado>;
  confirmarPlanoEvidencia(dto: PlanoEvidenciaConfirmarDTO, token: string): Promise<void>;
  listarPlanoEvidencias(planoItemId: string, token: string): Promise<PlanoEvidenciaRemota[]>;
  removerPlanoEvidencia(id: string, planoItemId: string, token: string): Promise<void>;
  /** Trilha de auditoria de evidências (admin/coord): uploads + exclusões. */
  auditoria(filtro: FiltroAuditoria, token: string): Promise<RespostaAuditoria>;
}

/** Erro de API com o status HTTP, para o chamador decidir (401 → re-login). */
/**
 * Resposta do push da supervisão. `aplicado: false` = escrita OBSOLETA: outro
 * dispositivo gravou depois da versão que este cliente leu (doc 20 §I6). Não é
 * erro — o cliente descarta o pendente e re-puxa, em vez de re-tentar.
 */
export interface RespostaSupervisao {
  readonly validado: boolean;
  readonly aplicado: boolean;
  readonly versao: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    super(mensagem);
    this.name = "ApiError";
  }
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Conversão segura para `UsuarioAutenticado` a partir do login remoto. */
export function paraUsuario(r: LoginResposta): UsuarioAutenticado {
  return {
    id: r.usuario.id,
    nome: r.usuario.nome,
    papel: r.usuario.papel as UsuarioAutenticado["papel"],
    funcao: r.usuario.eixo ? `Eixo ${r.usuario.eixo}` : r.usuario.papel,
  };
}

export class HttpApiClient implements ApiClient {
  constructor(
    private readonly baseUrl: string,
    // Envolvido numa arrow: passar `fetch` direto e chamá-lo como `this.fetchImpl(...)`
    // rebinda o `this` para a instância e o navegador lança "Illegal invocation"
    // (síncrono, antes de qualquer requisição). A chamada não-qualificada preserva
    // o binding global do `fetch`.
    private readonly fetchImpl: FetchLike = (input, init) => fetch(input, init),
  ) {}

  /** Handler de sessão expirada (401 autenticado); registrado pelo app. */
  private naoAutorizado: (() => void) | null = null;

  aoNaoAutorizado(handler: () => void): void {
    this.naoAutorizado = handler;
  }

  async login(matricula: string, senha: string): Promise<LoginResposta> {
    return this.json<LoginResposta>("POST", "/auth/login", { matricula, senha });
  }

  async push(operacoes: readonly OperacaoSyncDTO[], token: string): Promise<ResultadoPushItem[]> {
    const r = await this.json<{ resultados: ResultadoPushItem[] }>(
      "POST",
      "/sync/push",
      { operacoes },
      token,
    );
    return r.resultados;
  }

  async pull(desde: string | null, token: string): Promise<AvaliacaoRemota[]> {
    const qs = desde ? `?desde=${encodeURIComponent(desde)}` : "";
    const r = await this.json<{ avaliacoes: AvaliacaoRemota[] }>("GET", `/sync/pull${qs}`, undefined, token);
    return r.avaliacoes;
  }

  async pushSupervisao(dto: SupervisaoPushDTO, token: string): Promise<RespostaSupervisao> {
    return this.json<RespostaSupervisao>("POST", "/supervisao", dto, token);
  }

  async presignEvidencia(dto: EvidenciaUploadDTO, token: string): Promise<UploadEvidenciaPreparado> {
    return this.json<UploadEvidenciaPreparado>("POST", "/evidencias/presign", dto, token);
  }

  async confirmarEvidencia(dto: EvidenciaConfirmarDTO, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", "/evidencias/confirmar", dto, token);
  }

  async presignAssinatura(dto: AssinaturaUploadDTO, token: string): Promise<UploadPreparado> {
    return this.json<UploadPreparado>("POST", "/me/assinatura/presign", dto, token);
  }

  async confirmarAssinatura(dto: AssinaturaConfirmarDTO, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", "/me/assinatura/confirmar", dto, token);
  }

  async obterAssinatura(token: string): Promise<AssinaturaView | null> {
    return (await this.json<{ assinatura: AssinaturaView | null }>("GET", "/me/assinatura", undefined, token))
      .assinatura;
  }

  async removerAssinatura(token: string): Promise<void> {
    await this.json<{ ok: boolean }>("DELETE", "/me/assinatura", undefined, token);
  }

  async listarEvidencias(avaliacaoId: string, token: string): Promise<EvidenciaRemota[]> {
    const r = await this.json<{ evidencias: EvidenciaRemota[] }>(
      "GET",
      `/evidencias?avaliacaoId=${encodeURIComponent(avaliacaoId)}`,
      undefined,
      token,
    );
    return r.evidencias;
  }

  async removerEvidencia(id: string, avaliacaoId: string, token: string): Promise<void> {
    try {
      await this.json<{ ok: boolean }>(
        "DELETE",
        `/evidencias/${encodeURIComponent(id)}?avaliacaoId=${encodeURIComponent(avaliacaoId)}`,
        undefined,
        token,
      );
    } catch (e) {
      // 404 ⇒ já não existe: a exclusão é idempotente (retry da fila offline).
      if (e instanceof ApiError && e.status === 404) {
        return;
      }
      throw e;
    }
  }

  async listarUsuarios(token: string): Promise<UsuarioPublico[]> {
    return (await this.json<{ usuarios: UsuarioPublico[] }>("GET", "/usuarios", undefined, token)).usuarios;
  }

  async criarUsuario(dto: CriarUsuarioDTO, token: string): Promise<UsuarioPublico> {
    return (await this.json<{ usuario: UsuarioPublico }>("POST", "/usuarios", dto, token)).usuario;
  }

  async atualizarUsuario(id: string, dto: AtualizarUsuarioDTO, token: string): Promise<UsuarioPublico> {
    return (await this.json<{ usuario: UsuarioPublico }>("PATCH", `/usuarios/${id}`, dto, token)).usuario;
  }

  async senhaProvisoria(id: string, token: string): Promise<{ senha: string | null }> {
    return this.json<{ senha: string | null }>(
      "GET",
      `/usuarios/${encodeURIComponent(id)}/senha-provisoria`,
      undefined,
      token,
    );
  }

  async redefinirSenha(id: string, senha: string, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", `/usuarios/${id}/senha`, { senha }, token);
  }

  async trocarSenha(senhaAtual: string, novaSenha: string, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", "/me/senha", { senhaAtual, novaSenha }, token);
  }

  async listarCiclos(token: string): Promise<CicloPublico[]> {
    return (await this.json<{ ciclos: CicloPublico[] }>("GET", "/ciclos", undefined, token)).ciclos;
  }

  async abrirCiclo(dto: AbrirCicloDTO, token: string): Promise<{ id: string; avaliacaoId: string }> {
    return this.json<{ id: string; avaliacaoId: string }>("POST", "/ciclos", dto, token);
  }

  async encerrarCiclo(id: string, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", `/ciclos/${id}/encerrar`, {}, token);
  }

  async editarCiclo(id: string, dto: EditarCicloDTO, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", `/ciclos/${id}/editar`, dto, token);
  }

  async salvarEscopoCiclo(avaliacaoId: string, escopo: EscopoData, token: string): Promise<void> {
    await this.json<{ ok: boolean }>(
      "PUT",
      `/avaliacoes/${encodeURIComponent(avaliacaoId)}/escopo`,
      { escopo },
      token,
    );
  }

  async formularioPublico(token: string): Promise<FormularioPublico> {
    return this.json<FormularioPublico>("GET", `/publico/${encodeURIComponent(token)}`);
  }

  async enviarImgr(token: string, dto: ImgrRespostaDTO): Promise<void> {
    await this.json<{ ok: boolean }>("POST", `/publico/${encodeURIComponent(token)}/imgr`, dto);
  }

  async resultadoImgr(cicloId: string, token: string): Promise<DashboardImgr> {
    return (
      await this.json<{ resultado: DashboardImgr }>(
        "GET",
        `/ciclos/${encodeURIComponent(cicloId)}/imgr`,
        undefined,
        token,
      )
    ).resultado;
  }

  async obterMatriz(unidadeId: string, token: string): Promise<MatrizUnidade | null> {
    return (
      await this.json<{ matriz: MatrizUnidade | null }>(
        "GET",
        `/unidades/${encodeURIComponent(unidadeId)}/matriz`,
        undefined,
        token,
      )
    ).matriz;
  }

  async salvarMatriz(unidadeId: string, dto: MatrizUploadDTO, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", `/unidades/${encodeURIComponent(unidadeId)}/matriz`, dto, token);
  }

  async listarMatrizes(token: string): Promise<ResumoMatrizUnidade[]> {
    return (await this.json<{ matrizes: ResumoMatrizUnidade[] }>("GET", "/matrizes", undefined, token))
      .matrizes;
  }

  async enviarArquivo(url: string, arquivo: Blob, contentType: string): Promise<void> {
    const resp = await this.fetchImpl(url, {
      method: "PUT",
      body: arquivo,
      headers: { "content-type": contentType },
    });
    if (!resp.ok) {
      throw new ApiError(resp.status, "Falha ao enviar o arquivo ao armazenamento");
    }
  }

  async migracaoIngerir(proposta: unknown, token: string): Promise<{ loteId: string }> {
    return this.json<{ loteId: string }>("POST", "/migracao/ingerir", proposta, token);
  }
  async migracaoListar(token: string): Promise<LoteRemoto[]> {
    return (await this.json<{ lotes: LoteRemoto[] }>("GET", "/migracao/lotes", undefined, token)).lotes;
  }
  async migracaoLote(id: string, token: string): Promise<LoteDetalheRemoto> {
    return this.json<LoteDetalheRemoto>("GET", `/migracao/lotes/${encodeURIComponent(id)}`, undefined, token);
  }
  async migracaoImpacto(itemId: string, token: string): Promise<ImpactoRemoto> {
    return this.json<ImpactoRemoto>(
      "GET",
      `/migracao/impacto?itemId=${encodeURIComponent(itemId)}`,
      undefined,
      token,
    );
  }
  async migracaoDecidir(
    loteId: string,
    itemId: string,
    decisao: DecisaoMigracaoValor,
    codigoDestino: string | null,
    token: string,
  ): Promise<void> {
    await this.json<{ ok: boolean }>(
      "POST",
      `/migracao/lotes/${encodeURIComponent(loteId)}/itens/${encodeURIComponent(itemId)}/decisao`,
      { decisao, codigoDestino },
      token,
    );
  }
  async migracaoAprovar(id: string, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", `/migracao/lotes/${encodeURIComponent(id)}/aprovar`, {}, token);
  }
  async migracaoPreflight(id: string, token: string): Promise<PreflightRemoto> {
    return this.json<PreflightRemoto>(
      "GET",
      `/migracao/lotes/${encodeURIComponent(id)}/preflight`,
      undefined,
      token,
    );
  }
  async migracaoExecutar(id: string, token: string): Promise<ResultadoExecucaoRemoto> {
    return this.json<ResultadoExecucaoRemoto>(
      "POST",
      `/migracao/lotes/${encodeURIComponent(id)}/executar`,
      {},
      token,
    );
  }
  async migracaoReverter(id: string, token: string): Promise<ResultadoExecucaoRemoto["resumo"]> {
    return this.json<ResultadoExecucaoRemoto["resumo"]>(
      "POST",
      `/migracao/lotes/${encodeURIComponent(id)}/reverter`,
      {},
      token,
    );
  }
  async migracaoManifesto(id: string, token: string): Promise<ManifestoRemoto | null> {
    return (
      await this.json<{ manifesto: ManifestoRemoto | null }>(
        "GET",
        `/migracao/lotes/${encodeURIComponent(id)}/manifesto`,
        undefined,
        token,
      )
    ).manifesto;
  }

  async planosDaUnidade(token: string): Promise<PlanoAcao[]> {
    return (await this.json<{ planos: PlanoAcao[] }>("GET", "/planos", undefined, token)).planos;
  }

  async planoDaAvaliacao(avaliacaoId: string, token: string): Promise<PlanoAcao | null> {
    return (
      await this.json<{ plano: PlanoAcao | null }>(
        "GET",
        `/planos?avaliacaoId=${encodeURIComponent(avaliacaoId)}`,
        undefined,
        token,
      )
    ).plano;
  }

  async responderPlanoItem(id: string, dto: ResponderPlanoItemDTO, token: string): Promise<PlanoItem> {
    return (
      await this.json<{ item: PlanoItem }>("PATCH", `/planos/itens/${encodeURIComponent(id)}`, dto, token)
    ).item;
  }

  async regerarPlano(avaliacaoId: string, token: string): Promise<PlanoAcao> {
    return (
      await this.json<{ plano: PlanoAcao }>(
        "POST",
        `/planos/${encodeURIComponent(avaliacaoId)}/regerar`,
        {},
        token,
      )
    ).plano;
  }

  async relatorioDaUnidade(token: string): Promise<RelatorioDTO> {
    return (await this.json<{ relatorio: RelatorioDTO }>("GET", "/relatorio", undefined, token)).relatorio;
  }

  async relatorioDaAvaliacao(avaliacaoId: string, token: string): Promise<RelatorioDTO> {
    return (
      await this.json<{ relatorio: RelatorioDTO }>(
        "GET",
        `/relatorio?avaliacaoId=${encodeURIComponent(avaliacaoId)}`,
        undefined,
        token,
      )
    ).relatorio;
  }

  async presignPlanoEvidencia(
    dto: PlanoEvidenciaUploadDTO,
    token: string,
  ): Promise<UploadEvidenciaPreparado> {
    return this.json<UploadEvidenciaPreparado>("POST", "/planos/evidencias/presign", dto, token);
  }

  async confirmarPlanoEvidencia(dto: PlanoEvidenciaConfirmarDTO, token: string): Promise<void> {
    await this.json<{ ok: boolean }>("POST", "/planos/evidencias/confirmar", dto, token);
  }

  async listarPlanoEvidencias(planoItemId: string, token: string): Promise<PlanoEvidenciaRemota[]> {
    const r = await this.json<{ evidencias: PlanoEvidenciaRemota[] }>(
      "GET",
      `/planos/itens/${encodeURIComponent(planoItemId)}/evidencias`,
      undefined,
      token,
    );
    return r.evidencias;
  }

  async removerPlanoEvidencia(id: string, planoItemId: string, token: string): Promise<void> {
    try {
      await this.json<{ ok: boolean }>(
        "DELETE",
        `/planos/evidencias/${encodeURIComponent(id)}?planoItemId=${encodeURIComponent(planoItemId)}`,
        undefined,
        token,
      );
    } catch (e) {
      // 404 ⇒ já não existe: exclusão idempotente.
      if (e instanceof ApiError && e.status === 404) {
        return;
      }
      throw e;
    }
  }

  async auditoria(filtro: FiltroAuditoria, token: string): Promise<RespostaAuditoria> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filtro)) {
      if (v !== undefined && v !== null && v !== "") {
        qs.set(k, String(v));
      }
    }
    const s = qs.toString();
    return this.json<RespostaAuditoria>("GET", `/admin/auditoria${s ? `?${s}` : ""}`, undefined, token);
  }

  private async json<T>(metodo: string, rota: string, corpo?: unknown, token?: string): Promise<T> {
    const headers: Record<string, string> = {};
    if (corpo !== undefined) {
      headers["content-type"] = "application/json";
    }
    if (token) {
      headers.authorization = `Bearer ${token}`;
    }
    const resp = await this.fetchImpl(`${this.baseUrl}${rota}`, {
      method: metodo,
      headers,
      body: corpo === undefined ? undefined : JSON.stringify(corpo),
    });
    if (!resp.ok) {
      // 401 numa requisição AUTENTICADA (com token) = sessão expirada/ inválida:
      // dispara o handler global (logout → tela de login). O login em si não tem
      // token, então um 401 de credencial errada não cai aqui e não desloga.
      if (resp.status === 401 && token) {
        this.naoAutorizado?.();
      }
      const msg = await mensagemDeErro(resp);
      throw new ApiError(resp.status, msg);
    }
    return (await resp.json()) as T;
  }
}

async function mensagemDeErro(resp: Response): Promise<string> {
  try {
    const corpo = (await resp.json()) as { erro?: string };
    return corpo.erro ?? `HTTP ${resp.status}`;
  } catch {
    return `HTTP ${resp.status}`;
  }
}

const BASE_PADRAO = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || "/api";

/** Instância padrão usada pelo app (base de `VITE_API_URL`, default `/api`). */
export const apiClient: ApiClient = new HttpApiClient(BASE_PADRAO);
